/**
 * Development Console Utilities
 * Provides conditional console logging that respects environment settings
 * 
 * USAGE GUIDELINES:
 * =================
 * 
 * For Development/Debug Logs (stripped in production):
 *   devConsole.log('User clicked button', buttonData);
 *   devConsole.debug('Debugging info', debugData);
 * 
 * For Important Warnings (shown in dev, optionally in prod):
 *   devConsole.warn('API timeout occurred');
 * 
 * For Critical Errors (always shown):
 *   devConsole.error('Fatal error occurred', errorObject);
 * 
 * PRODUCTION BEHAVIOR:
 * ====================
 * - With npm run build:prod → NO console logs visible to end users
 * - With npm run build:dev → All console logs visible
 * - With npm start → All console logs visible
 * 
 * Configure via .env files:
 * - .env.production: REACT_APP_ENABLE_PROD_CONSOLE=false
 * - .env.dev: REACT_APP_ENABLE_PROD_CONSOLE=true
 */

import { FEATURE_FLAGS } from '../config/featureFlags';

// No-op function for production
const noop = () => {};

export const devConsole = {
  /**
   * Debug logging - only shows in development
   * Completely stripped in production builds
   */
  debug: FEATURE_FLAGS.ENABLE_DEBUG_LOGGING 
    ? (...args: any[]) => console.debug('[DEV]', ...args)
    : noop,

  /**
   * Info/Log - shows in development and optionally in production
   * Stripped in production unless REACT_APP_ENABLE_PROD_CONSOLE=true
   */
  log: (FEATURE_FLAGS.ENABLE_DEBUG_LOGGING || FEATURE_FLAGS.ENABLE_PRODUCTION_CONSOLE_LOGS)
    ? (...args: any[]) => console.log('[INFO]', ...args)
    : noop,

  /**
   * Warning logging - shows in development always, optionally in production
   * Can be configured to show in production for important warnings
   */
  warn: (FEATURE_FLAGS.ENABLE_DEBUG_LOGGING || FEATURE_FLAGS.ENABLE_PRODUCTION_CONSOLE_LOGS)
    ? (...args: any[]) => console.warn('[WARN]', ...args)
    : noop,

  /**
   * Error logging - shows in development always, optionally in production
   * Critical errors can still be logged server-side via structured logging
   */
  error: (FEATURE_FLAGS.ENABLE_DEBUG_LOGGING || FEATURE_FLAGS.ENABLE_PRODUCTION_CONSOLE_LOGS)
    ? (...args: any[]) => console.error('[ERROR]', ...args)
    : noop,

  /**
   * Conditional logging based on environment
   * Development: Full logging
   * Production: Controlled by feature flag
   */
  conditionalLog: (message: string, data?: any) => {
    if (FEATURE_FLAGS.ENABLE_DEBUG_LOGGING) {
      console.log(`[DEV] ${message}`, data || '');
    } else if (FEATURE_FLAGS.ENABLE_PRODUCTION_CONSOLE_LOGS) {
      console.log(`[PROD] ${message}`);
    }
    // Otherwise: no output (production with console disabled)
  }
};

/**
 * Production-safe console replacement
 * Use this as a drop-in replacement for direct console.* calls
 */
export const safeConsole = {
  log: FEATURE_FLAGS.ENABLE_DEBUG_LOGGING ? console.log.bind(console) : noop,
  info: FEATURE_FLAGS.ENABLE_DEBUG_LOGGING ? console.info.bind(console) : noop,
  warn: FEATURE_FLAGS.ENABLE_DEBUG_LOGGING ? console.warn.bind(console) : noop,
  error: FEATURE_FLAGS.ENABLE_DEBUG_LOGGING ? console.error.bind(console) : noop,
  debug: FEATURE_FLAGS.ENABLE_DEBUG_LOGGING ? console.debug.bind(console) : noop,
};

export default devConsole;
